import { useCallback, useEffect, useState } from 'react';
import { QUALITY_CHECK_DATA } from 'constants/constants';
import AdapterError from 'errors/AdapterError';
import { getItemFromSS } from 'utils/storage';

// --- Interfaces ---

interface QuestionValue {
    Ids?: any[];
    Value?: string | number;
}

interface ServerQuestion {
    Id: string | number;
    Type: 'SingleAnswer' | 'FreeText' | 'MultiAnswer' | string;
    Value?: QuestionValue;
}

interface ServerGroup {
    Id: string | number;
    Steps?: Array<{
        Questions?: ServerQuestion[];
    }>;
}

interface ServerData {
    Groups?: ServerGroup[];
}

interface SaveDataParams {
    groupId: string | number;
    questions: Array<{
        questionId?: string | number;
        value: any;
    }>;
}

type FormDataState = Record<string, any>;

// --- Hook ---

export const useFormData = ({ serverData }: { serverData?: ServerData }) => {
    const [data, setData] = useState<FormDataState>({});
    const [isInitDone, setInitDone] = useState<boolean>(false);

    const getDataFromSS = useCallback((): FormDataState | null => getItemFromSS(QUALITY_CHECK_DATA), []);

    const initializeData = useCallback(({ groupIds }: { groupIds: Array<string | number> }) => {
        if (!groupIds) throw new AdapterError('There is no groupIds for initializeData function');

        const newData = groupIds.reduce((acc, key) => ({ ...acc, [key]: {} }), {});

        setData(newData);
        sessionStorage.setItem(QUALITY_CHECK_DATA, JSON.stringify(newData));
    }, []);

    const saveData = useCallback(({ groupId, questions }: SaveDataParams) => {
        if (!groupId) throw new AdapterError('There is no groupId for saveData function');

        setData((prevData) => {
            const newData = { ...prevData };

            if (!newData[groupId]) {
                newData[groupId] = {};
            }

            questions?.forEach((question) => {
                const { questionId, value } = question;

                if (questionId) {
                    newData[groupId][questionId] = value;
                } else {
                    newData[groupId] = {
                        ...newData[groupId],
                        value,
                    };
                }
            });

            sessionStorage.setItem(QUALITY_CHECK_DATA, JSON.stringify(newData));

            return newData;
        });
    }, []);

    const clearData = useCallback(() => {
        setData({});
        sessionStorage.removeItem(QUALITY_CHECK_DATA);
    }, []);

    useEffect(() => {
        if (!serverData) return;

        const group = serverData?.Groups?.[1];
        const questions = group?.Steps?.[0]?.Questions;

        const serverValues: Record<string, any> = {
            [group?.Id as string | number]: questions?.reduce((capacitor: Record<string, any>, question) => {
                if (question?.Value) {
                    if (question.Type === 'SingleAnswer') {
                        capacitor[question.Id] = question.Value.Ids?.[0];
                    } else if (question.Type === 'FreeText') {
                        capacitor[question.Id] = question.Value.Value;
                    } else {
                        capacitor[question.Id] = question.Value.Ids;
                    }
                }

                return capacitor;
            }, {}),
        };

        const dataFromSS = getDataFromSS();
        const groupId = group?.Id;

        if (groupId) {
            const mergedData = {
                [groupId]: {
                    ...serverValues?.[groupId],
                    ...dataFromSS?.[groupId],
                },
            };

            if (dataFromSS || serverValues[groupId]) {
                setData(mergedData);
            }
        }

        setInitDone(true);
    }, [getDataFromSS, serverData]);

    return {
        data,
        getDataFromSS,
        initializeData,
        saveData,
        clearData,
        isInitDone,
    };
};
